import { ChannelType } from "../../Channel/ChannelDataTypes";
import { AppThunk, ApiError } from "../misc";
import { doGetLayout, doPanToCell } from "./channelLayout";
import { doGetLayoutCells } from "./cells";
import { doLogoutUser } from "./user";
import { doRoute } from "./location";
import { doSaveLastChannel } from "./channelSettings";
import { ActiveChannelType, ChannelFilterType } from "../reducers/channels";
import { ClusterApi } from "@cluster/web-components";
import { selectAlerts } from "../selectors/alerts";
import { ROUTE_ACTIONS } from "../routes";
import firebase from '../../lib/firebase';
import moment from 'moment'
import { selectChannelFilters } from "../selectors";

export enum CHANNEL_ACTIONS {
  FETCH_CHANNELS_START = "FETCH_CHANNELS_START",
  FETCH_CHANNELS_SUCCESS = "FETCH_CHANNELS_SUCCESS",
  FETCH_CHANNELS_ERROR = "FETCH_CHANNELS_ERROR",
  ACTIVE_CHANNEL_SET = "ACTIVE_CHANNEL_SET",
  ALERT_ADDED = "ALERT_ADDED",
}

export type CHANNEL_ACTION_TYPES = FetchChannelsAction | SetActiveChannelAction | AddAlertAction

export type FetchChannelsAction = {
  type: CHANNEL_ACTIONS.FETCH_CHANNELS_START | CHANNEL_ACTIONS.FETCH_CHANNELS_SUCCESS | CHANNEL_ACTIONS.FETCH_CHANNELS_ERROR,
  payload?: ChannelType[],
  error?: ApiError
}

export type SetActiveChannelAction = {
  type: CHANNEL_ACTIONS.ACTIVE_CHANNEL_SET,
  payload: ActiveChannelType,
}

export type AddAlertAction = {
  type: CHANNEL_ACTIONS.ALERT_ADDED,
  payload: ClusterApi.Alert & {id: number}
}

export const doLoadChannel = (channelId: number, centroidId?: number, familyTag?: string, panToCell?: number | false, filters?: ChannelFilterType): AppThunk => async (dispatch, getState) => {
  const state = getState();
  let channelChanged = true;

  // Check if the channel is already loaded.
  if (state.channelLayout.data.boardId === channelId
    && state.channels.activeChannel.filters === filters) {
    channelChanged = false
  }

  let eventParams: {[key: string]: any;} = {};
  if (typeof centroidId !== "undefined") {
    eventParams.centroid = centroidId;
  }
  if (typeof channelId !== "undefined") {
    eventParams.channel_id = channelId;
  }
  if (typeof familyTag !== "undefined") {
    eventParams.family_tag = familyTag;
  }
  if (state.channelLayout.pannedCell !== false) {
    eventParams.initialCellId = state.channelLayout.pannedCell;
  }

  // Firebase event
  firebase.analytics.logEvent("load_channel", eventParams);

  // Set the active channel configuration
  dispatch(doSetActiveChannel(channelId, centroidId, familyTag, filters));

  // Get a new cluster layout.
  await dispatch(doGetLayout(channelId, centroidId, familyTag, filters?.favorites, filters?.recent));

  // Pan to cell after layout loads
  if (panToCell) {
    dispatch(doPanToCell(panToCell));
  }

  // Save the last loaded channel into settings.
  dispatch(doSaveLastChannel(channelId, centroidId, familyTag));

  // If we're loading a new channel, load the cells in. Otherwise keep the existing cells.
  if (channelChanged) {
    await dispatch(doGetLayoutCells());
  }

}

export const doGetChannels = (): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  dispatch({type: CHANNEL_ACTIONS.FETCH_CHANNELS_START});
  try {
    let data = await api.getBoards();

    dispatch({
      type: CHANNEL_ACTIONS.FETCH_CHANNELS_SUCCESS,
      payload: data
    });

    return Promise.resolve();

  } catch (message) {
    try {
      const error = JSON.parse(message.message);

      dispatch({
        type: CHANNEL_ACTIONS.FETCH_CHANNELS_ERROR,
        error
      });

      if (error.errorCode === "AUTH_INVALID_TOKEN" || error.errorCode === "AUTH_HEADER_MISSING" || error.errorCode === "AUTH_EXPIRED_TOKEN") {
        dispatch(doLogoutUser("Your session has expired."));
      }
    } catch(badError) {
      dispatch({
        type: CHANNEL_ACTIONS.FETCH_CHANNELS_ERROR,
        error: {
          developerMessage: message.message,
          userMessage: "An unknown error occured"
        }
      });
    }
  }
}

export const doSetActiveChannel = (channelId: number, centroidId?: number, familyTag?: string, filters?: ChannelFilterType) => {
  return {
    type: CHANNEL_ACTIONS.ACTIVE_CHANNEL_SET,
    payload: {
      channelId,
      centroidId,
      familyTag,
      filters
    }
  }
}

// Changes the route for channels but leaves unrelated query tags as is.
export const doChangeChannelRoute = (channelId: number, options?: {centroidId?: number, familyTag?: string, panToCell?: number, filters?: ChannelFilterType}) => {
  return doRoute(ROUTE_ACTIONS.CHANNEL, {payload: { id: channelId }, query: { centroidId: options?.centroidId?.toString(), familyTag: options?.familyTag, pan: options?.panToCell?.toString(), filters: JSON.stringify(options?.filters) }});
}

export const doAddNewAlert = (alert: AddAlertAction["payload"]): AppThunk => async (dispatch, getState, api) => {
  const state = getState();
  const alerts = selectAlerts(state);

  // If the alert is expired, just ignore it.
  if (moment(alert.expires).isAfter(new Date())) {
    // Avoid dupes by ignoring IDs that already exist.
    if (!alerts.find(currentAlert => currentAlert.id === alert.id)) {
      dispatch({
        type: CHANNEL_ACTIONS.ALERT_ADDED,
        payload: alert
      });
    }
  }
}

export const doChangeFilter = (filter: keyof ChannelFilterType, value: boolean) => {
  let filters:ChannelFilterType = {};
  filters[filter] = value;

  return doChangeFilters(filters);
}

export const doChangeFilters = (filters: ChannelFilterType): AppThunk => (dispatch, getState) => {
  const state = getState();
  let { type, payload, query } = state.location;

  if (typeof query === "undefined") {
    query = {};
  }

  const currentFilters = {...selectChannelFilters(state)};

  let newFilters = Object.assign(currentFilters, filters);

  // Remove filters entirely if false
  Object.keys(newFilters).forEach((key) => {
    if(newFilters[key as keyof ChannelFilterType] === false) {
      delete newFilters[key as keyof ChannelFilterType];
    }
  })

  if (Object.keys(newFilters).length > 0) {
    query.filters = JSON.stringify(newFilters);
  } else {
    if (typeof query.filters !== "undefined") {
      query.filters = undefined;
    }
  }

  return dispatch(doRoute(type as ROUTE_ACTIONS, {payload, query}, true));
}